import { Add, Delete, Edit, MoreVert } from '@mui/icons-material';
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
} from '@mui/material';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { MutableRefObject, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMenuButton } from '~/hooks/useMenu';
import { UseReportDatasetOutput } from '~/scenes/PortalModules/Analytics/common';
import { IncludedSavedSettings } from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/data';
import { SavedReportSettingsDialog } from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/SaveReportSettingsDialog';
import { UseSavedReportSettingsConfigOutput } from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/useSavedReportSettingsConfig';
import { useSavedReportSettingsSelector } from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/useSavedReportSettingsSelector';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/useStyles';

type SavedSettingOption = {
  id: string;
  name: string;
  category: string;
  secondary?: string;
  savedSettings?: IncludedSavedSettings;
};

type PropsType<P> = UseSavedReportSettingsConfigOutput<P> & {
  apiRef: MutableRefObject<GridApiPremium | null>;
  reportDataset: UseReportDatasetOutput<P>;
  isLoading?: boolean;
};

export const SavedReportSettingsSelector = <P,>(props: PropsType<P>) => {
  const {
    reportSettingOptions,
    selectedReportSettings,
    onChangeSelectedReport,
    onCreateReportSettings,
    onUpdateSelectedReportSettings,
    onDeleteSelectedReportSettings,
    apiRef,
    reportDataset,
    isLoading,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { selectedReportId, options, canEditSelectedSettings, selectField } =
    useSavedReportSettingsSelector({
      selectedReportSettings,
      reportSettingOptions,
    });

  // Dialog State
  const [dialogMode, setDialogMode] = useState<string | null>(null);
  const { isOpen, open, close, anchorEl } = useMenuButton();

  const onOpenDialog = useCallback(
    (dialogMode: string | null) => {
      setDialogMode(dialogMode);
      close();
    },
    [setDialogMode, close],
  );

  // Render Menu Items
  const renderMenuItemsGroup = useCallback(
    (options: SavedSettingOption[], groupKey: string, groupLabel?: string) => {
      const subheader =
        !!groupLabel && options.length > 0
          ? [
              <Divider key={`saved-settings-${groupKey}-divider`} />,
              <ListSubheader
                key={`saved-settings-${groupKey}-subheader`}
                sx={sxStyles.listSubHeader}
              >
                {groupLabel}
              </ListSubheader>,
            ]
          : [];

      const menuItems = options.map((option, index) => {
        const { id, name } = option;

        return (
          <MenuItem value={id} key={`saved-settings-${groupKey}-${index}`}>
            <ListItemText>{name}</ListItemText>
          </MenuItem>
        );
      });

      return subheader.concat(menuItems);
    },
    [sxStyles],
  );

  return (
    <Box sx={sxStyles.root}>
      <FormControl variant={'outlined'} fullWidth={true}>
        <InputLabel id={selectField.labelId} size={'small'}>
          {selectField.label}
        </InputLabel>
        <Select
          labelId={selectField.labelId}
          variant={'outlined'}
          name={'selectSavedSettings'}
          label={selectField.label}
          value={selectedReportId}
          onChange={(event) => onChangeSelectedReport(event.target.value)}
          size={'small'}
          disabled={options.all.length < 2 || isLoading}
          renderValue={(selectedId) => {
            return options.all.find(({ id }) => id === selectedId)?.name;
          }}
          sx={sxStyles.select}
        >
          {[
            renderMenuItemsGroup(options.default, 'default'),
            renderMenuItemsGroup(options.shared, 'shared', t('shared')),
            renderMenuItemsGroup(
              options.personal,
              'personal',
              t('laPersonalReportSettings'),
            ),
          ].flat()}
        </Select>
      </FormControl>

      <IconButton
        id={'report-save-settings-button'}
        aria-controls={isOpen ? 'basic-menu' : undefined}
        aria-haspopup={'true'}
        aria-expanded={isOpen ? 'true' : undefined}
        disabled={isLoading}
        onClick={open}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id={'report-save-settings-menu'}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={close}
        slotProps={{
          list: {
            'aria-labelledby': 'report-save-settings-button',
            dense: true,
          },
        }}
      >
        {canEditSelectedSettings && [
          <ListSubheader key={'selectedHeader'} sx={sxStyles.listSubHeader}>
            {selectField.label}
          </ListSubheader>,
          <ListItemButton
            key={'edit'}
            onClick={() => {
              onOpenDialog('edit');
            }}
          >
            <ListItemIcon>
              <Edit />
            </ListItemIcon>
            <ListItemText>{t('edit')}</ListItemText>
          </ListItemButton>,
          <ListItemButton
            key={'delete'}
            onClick={() => {
              onDeleteSelectedReportSettings();
            }}
          >
            <ListItemIcon>
              <Delete />
            </ListItemIcon>
            <ListItemText>{t('delete')}</ListItemText>
          </ListItemButton>,
          <Divider key={'divider'} />,
        ]}

        <ListItemButton
          onClick={() => {
            onOpenDialog('new');
          }}
        >
          <ListItemIcon>
            <Add />
          </ListItemIcon>
          <ListItemText>{t('create')}</ListItemText>
        </ListItemButton>
      </Menu>

      {!!dialogMode && (
        <SavedReportSettingsDialog
          open={!!dialogMode}
          onClose={() => {
            setDialogMode(null);
          }}
          selectedReportSettings={selectedReportSettings}
          onCreateReportSettings={onCreateReportSettings}
          onUpdateSelectedReportSettings={onUpdateSelectedReportSettings}
          apiRef={apiRef}
          reportDataset={reportDataset}
          isEdit={dialogMode === 'edit'}
        />
      )}
    </Box>
  );
};
